import React, { useState, useEffect } from "react";
import { Chart } from "react-google-charts";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { Button, Card, Empty, Select, Row, Col, Spin } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

dayjs.extend(isoWeek); // ensures week starts Monday

const { Option } = Select;

function Attendance() {
  const navigate = useNavigate();
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState(0); // 0 = Weekly, 1 = Monthly
  const [dateRange, setDateRange] = useState(null);

  const employeeId = localStorage.getItem("personalId");

  const fetchAttendanceSummary = async (type) => {
    if (!employeeId) return;
    setLoading(true);

    try {
      let startDate, endDate;

      if (type === 0) { // Weekly
        startDate = dayjs().startOf("isoWeek").format("YYYY-MM-DD"); // Monday
        endDate = dayjs().endOf("isoWeek").format("YYYY-MM-DD"); // Sunday
      } else { // Monthly
        startDate = dayjs().startOf("month").format("YYYY-MM-DD");
        endDate = dayjs().endOf("month").format("YYYY-MM-DD");
      }

      setDateRange({ startDate, endDate });

      const url = API.attendance.userAttendance(employeeId, type, startDate, endDate);
      const response = await axiosInstance.get(url);

      const records = Array.isArray(response.data.records) ? response.data.records : [];

      const presentCount = records.filter(r => r.status === 1).length;
      const absentCount = records.filter(r => r.status === 2).length;
      const leaveOrHolidayCount = records.filter(r => r.status === 4).length;
      const lateCount = records.filter(r => r.isLate).length;
      const weekendCount = 0;

      setAttendanceSummary({
        presentCount,
        absentCount,
        leaveOrHolidayCount,
        lateCount,
        weekendCount,
        rangeText:
          type === 0
            ? `Week: ${dayjs(startDate).format("DD MMM")} → ${dayjs(endDate).format("DD MMM")}`
            : `Month: ${dayjs(startDate).format("MMMM YYYY")}`,
        viewType: type,
        totalEmployees: records.length,
      });

    } catch (error) {
      console.error("Error fetching attendance:", error);
      setAttendanceSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceSummary(viewType);
  }, [employeeId, viewType]);

  const chartData = [
    ["Status", "Count"],
    ["Present", attendanceSummary?.presentCount || 0],
    ["Absent", attendanceSummary?.absentCount || 0],
    ["Late Arrival", attendanceSummary?.lateCount || 0],
    ["Leave/Holiday", attendanceSummary?.leaveOrHolidayCount || 0],
    ["Weekend", attendanceSummary?.weekendCount || 0],
  ];

  return (
    <Card
      className="card-professional"
      title="Attendance Summary"
      extra={
        <Button onClick={() => navigate("/employeedashboard/attendance")}>
          <i className="fa fa-paper-plane" aria-hidden="true"></i> View
        </Button>
      }
      style={{ width: "100%", height: "100%" }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Select
            value={viewType}
            onChange={value => setViewType(value)}
            style={{ width: 160 }}
          >
            <Option value={0}>Weekly View</Option>
            <Option value={1}>Monthly View</Option>
          </Select>
        </Col>
      </Row>

      <div style={{ width: "100%", maxWidth: "900px", height: "300px", textAlign: "center" }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <Spin size="large" />
          </div>
        ) : attendanceSummary ? (
          <>
            <Chart
              chartType="PieChart"
              data={chartData}
              options={{
                pieHole: 0.4,
                pieSliceText: "value",
                tooltip: { text: "value" },
                colors: ["#52c41a", "#ff4d4f", "#faad14", "#40af14", "#722ed1"],
              }}
              width={"100%"}
              height={"100%"}
            />
            <div style={{ marginTop: "10px", fontSize: "13px", color: "#666" }}>
              {attendanceSummary.rangeText}
            </div>
          </>
        ) : (
          <Empty description="No Attendance information available" />
        )}
      </div>
    </Card>
  );
}

export default Attendance;